import { z } from 'zod'

import { isoDateTime } from '../lib/date'
import { titleCase } from '../lib/text'

/** Delivery channel for a client-facing message. */
export const communicationChannelSchema = z.enum(['sms', 'email'])
export type CommunicationChannel = z.infer<typeof communicationChannelSchema>

/**
 * A recorded outbound message to a client. Mirrors the `communications` table —
 * metadata only, since the body itself lives in the Customer.io template.
 */
export const communicationSchema = z.object({
  id: z.uuid(),
  memberId: z.uuid(),
  channel: communicationChannelSchema,
  /**
   * The message's catalogue key in the backend's `EMAILS`/`SMS` manifests — e.g.
   * `'bookingConfirmation'`. Typed as a plain string rather than a union: the
   * catalogues are backend-only, and shared must not have to be redeployed in
   * lockstep with a new template. `formatCommunicationType` resolves the label.
   */
  type: z.string(),
  /** Phone or email as handed to the provider. */
  toAddress: z.string(),
  /** When the provider accepted the message. Only sent rows reach the client, so never null here. */
  sentAt: isoDateTime,
  providerMessageId: z.string().nullable(),
  appointmentId: z.uuid().nullable(),
  /** The pet the message exclusively concerned, or null for member-level messages. */
  patientId: z.uuid().nullable(),
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
})

export type Communication = z.output<typeof communicationSchema>

/**
 * Human-readable labels for the backend message catalogues, keyed by catalogue key.
 *
 * Deliberately a loose `Record<string, string>` — the keys live in the backend's
 * `services/communications/email.ts` and `sms.ts`, which shared can't import.
 * `services/communications/labels.test.ts` in the backend asserts every catalogue key appears
 * here, so a new template can't silently ship unlabelled.
 */
export const COMMUNICATION_TYPE_LABELS: Record<string, string> = {
  // Emails
  bookingConfirmation: 'Booking Confirmation',
  bookingCancellation: 'Booking Cancellation',
  bookingReschedule: 'Booking Rescheduled',
  orderCollection: 'Order Ready for Collection',
  invoicePaymentLink: 'Invoice Payment Link',
  procedurePaymentLink: 'Procedure Payment Link',
  authOtpEmail: 'Login Code',
  oneWeekTreatmentReminder: 'Treatment Reminder (1 Week)',
  // SMS
  twoDayReminder: 'Appointment Reminder (2 Days)',
  dayBeforeReminder: 'Appointment Reminder (Day Before)',
  twoHourReminder: 'Appointment Reminder (2 Hours)',
  visitFeedback: 'Visit Feedback Request',
  firstVisitFeedback: 'First Visit Feedback Request',
  authOtpSms: 'Login Code',
}

/**
 * Resolves a communication's catalogue key to a display label, falling back to a
 * title-cased form of the key so an unlabelled new template still reads sensibly
 * instead of rendering blank.
 */
export function formatCommunicationType(type: string): string {
  return COMMUNICATION_TYPE_LABELS[type] ?? titleCase(type)
}
